import { DateTime } from 'luxon';
import { CalendarEventTypeMeetingType } from '@codegen-basetypes';

export enum CalendarEventTypes {
  CALL = '2',
  KICKOFF_CALL = '3',
  DESIGN_REVIEW = '4',
  TOUCH_BASE = '5',
  CONCEPT_REVIEW = '6',
  IDEA_REVIEW = '7',
  VIRTUAL_PROJECT_KICKOFF = '8',
  IN_PERSON_HOME_MEETING = '9',
  VIRTUAL_CHECK_IN = '10',
  IN_PERSON_CHECK_IN = '11'
}

export enum CalendarEventStatus {
  CANCELED = 'CANCELED',
}

export enum ScheduleCallSteps {
  INITIAL = 'initial',
  INITIAL_DESIGN_REVIEW = 'initial_design_review',
  CALL_SCHEDULER = 'call_scheduler',
  CALL_CONFIRMATION = 'call_confirmation',
  CANCEL_CALL = 'cancel_call',
  CANCELLED_CALL_CONFIRMATION = 'cancel_call_confirmation',
  RESCHEDULE_CALL = 'reschedule_call',
  RESCHEDULE_CALL_CONFIRMATION = 'reschedule_call_confirmation',
  CHOOSE_NEW_DESIGNER = 'choose_new_designer',
  MY_MEETINGS = 'my_meetings',
  DESIGN_NEXT_STEPS = 'design_next_steps'
}

export enum SelectableTimezones {
  HAWAII = 'US/Hawaii',
  ALASKA = 'US/Alaska',
  PACIFIC = 'US/Pacific',
  MOUNTAIN = 'US/Mountain',
  CENTRAL = 'US/Central',
  EASTERN = 'US/Eastern',
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

export interface BookableTimes {
  designer: {
    id: number;
    calendarId: number;
  };
  eventType: {
    id: string;
    title: string;
    durationSeconds: number;
    preBufferSeconds: number;
    postBufferSeconds: number;
    cancellationWindowSeconds: number;
  };
  availableTimeSlots: TimeSlot[];
}

export interface ICalendarEventType {
  id: string;
  displayName: string;
  title: string;
  meetingType: CalendarEventTypeMeetingType;
}

export interface ICalendarAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postalCode: string;
}

export interface ICalendarEvent {
  id: string;
  calendarId: string;
  endTime: string;
  startTime: string;
  status: string;
  eventType: ICalendarEventType;
  videoCall?: {
    joinUrl: string;
  } | null;
  address?: ICalendarAddress | null;
  roomId?: string;
  designer?: {
    id: string;
    user?: {
      firstName: string;
    } | null;
  };
}

export interface IScheduleCallDesigner {
  id: string;
  firstName?: string | null;
  // user: {
  //   firstName: string;
  // };
  photo?: string | null;
}

export const formatCalendarEventTime = (
  calendarEvent: ICalendarEvent,
  eventTimezone: SelectableTimezones
) => {
  const date = DateTime.fromISO(calendarEvent.startTime, { zone: eventTimezone });
  const day = date.toFormat('DDDD');
  const startTime = date.toFormat('h:mma');
  const endTime = DateTime.fromISO(calendarEvent.endTime, {
    zone: eventTimezone,
  })
    .toFormat('h:mma');

  return `${startTime} to ${endTime}, ${day}`;
};

export const getParsedTimezone = (timezone: SelectableTimezones) => {
  let timezoneText = '';
  switch (timezone) {
    case SelectableTimezones.HAWAII:
      timezoneText = `Hawaii Time`;
      break;

    case SelectableTimezones.ALASKA:
      timezoneText = `Alaska Time`;
      break;

    case SelectableTimezones.PACIFIC:
      timezoneText = `Pacific Time`;
      break;

    case SelectableTimezones.MOUNTAIN:
      timezoneText = `Mountain Time`;
      break;

    case SelectableTimezones.CENTRAL:
      timezoneText = `Central Time`;
      break;

    case SelectableTimezones.EASTERN:
      timezoneText = `Eastern Time`;
      break;

    default:
      break;
  }

  return timezoneText;
};

/**
 * @link https://github.com/moment/luxon/blob/master/docs/zones.md
 * @link https://en.wikipedia.org/wiki/List_of_tz_database_time_zones#ANCHORAGE
 *
 * We're using Luxon's zones functionality which give us a string
 * like 'Central Standard Time', from there we split by ' ' (space) and convert that
 * string to uppercase to match one of our SelectableTimezones.
 *
 * There is one special case for Hawaii time, the string comes as
 * 'Hawaii-Aleutian Standard Time', that's why we also split by '-' (dash)
 *
 * If we're not able to find the SelectableZone we will default to Mountain time
 */
export const getUserTimezone = () => {
  const dt = DateTime.local();

  let userTimezone;

  if (dt.offsetNameLong) {
    const timeZoneOffsetName: string
      = dt.offsetNameLong.split(' ')[0].split('-')[0].toUpperCase();

    userTimezone = SelectableTimezones[timeZoneOffsetName as keyof typeof SelectableTimezones];
  }

  return userTimezone ?? SelectableTimezones.MOUNTAIN;
};

export const isLegacyCallEvent = (eventType: ICalendarEventType) => {
  return eventType.id === CalendarEventTypes.CALL;
};
